/**
 * Customer 360 — consolidated cross-channel profile for one client.
 *
 * Route: /agent/customers/:clientId
 *
 * Layout:
 *   ┌ Header: avatar · name · status · email/phone/group · Open in Clients ┐
 *   ├ KPI tiles: open concerns · conversations · calls · chats · vmail · CSAT ┤
 *   ├ Known identities (channel handle chips) ┤
 *   ├ Tabs: Timeline | Related concerns ┤
 *   └   Timeline  — every touch point (tickets, calls, live chats, voicemails, CSAT)
 *       Related   — server-suggested link/merge candidates, agent confirms
 *
 * Suggestions are advisory only: Link/merge go through the same endpoints the
 * inbox uses, so a wrong guess is never irreversible automation.
 */
import { useCallback, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Contact,
  Copy,
  GitMerge,
  History,
  Link2,
  Loader2,
  Lock,
  LockOpen,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  PhoneCall,
  RefreshCw,
  ScanSearch,
  ShieldCheck,
  Star,
  UserRound,
  Users,
  Voicemail,
  X,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { formatDuration } from '../../../utils/formatDate.js'
import CreateClientPanel from '../../../components/agent/clients/CreateClientPanel.js'
import MergeDrawer, { type MergeCandidate, type MergeSubmit } from '../../../components/conversations/detail/MergeDrawer.js'
import { useConversationActions } from '../../../components/conversations/detail/useConversationActions.js'
import DatePicker from '../../../components/ui/DatePicker.js'

/* ── API shapes ─────────────────────────────────────────────────────── */

interface OverviewResponse {
  client: {
    id: string
    fullName: string | null
    email: string | null
    phone: string | null
    status: boolean
    locked: boolean
    groupName: string | null
  } | null
  identities: { channel: string; handle: string; source: string }[]
  stats: {
    totalConversations: number
    openConversations: number
    totalCalls: number
    totalLiveChats: number
    totalVoicemails: number
    avgCsat: number | null
    csatCount: number
    firstContactAt: string | null
    lastContactAt: string | null
  }
  conversationsByChannel: { channel: string; count: number }[]
}

interface FormSubmissionField {
  id: string
  type: string
  label: string
  excludeFromTimeline?: boolean
}

interface FormSubmissionData {
  formName: string
  fields: FormSubmissionField[] | null
  values: Record<string, unknown>
  attachments: { id: string; fieldId: string; filename: string; mimeType: string; sizeBytes: number }[]
}

interface TimelineItem {
  id: string
  type: 'conversation' | 'call' | 'live_chat' | 'voicemail' | 'csat' | 'form_submission'
  conversationId: string | null
  callId: string | null
  ref: string | null
  title: string
  channel: string
  at: string
  status: string | null
  actor: string | null
  durationSec: number | null
  isMerged: boolean
  formData: FormSubmissionData | null
}

interface TimelineResponse {
  items: TimelineItem[]
  total: number
  page: number
  limit: number
}

interface RelatedSide {
  id: string
  referenceId: string
  subject: string | null
  status: string | null
  channel: string | null
  openedAt: string
}

interface RelatedItem {
  a: RelatedSide
  b: RelatedSide
  reasons: string[]
  score: number
  suggestedAction: 'merge' | 'link'
}

interface CustomerHistoryItem {
  id: string
  actorName: string | null
  action: string
  resourceType: string | null
  resourceId: string | null
  createdAt: string
  payload: Record<string, unknown> | null
}

interface CustomerHistoryResponse {
  rows: CustomerHistoryItem[]
  total: number
  limit: number
  offset: number
}

/* ── Presentation bits ──────────────────────────────────────────────── */

const R = 'rounded-[3px]'

type TimelineType = 'all' | 'conversation' | 'call' | 'live_chat' | 'voicemail' | 'csat' | 'form_submission'
const HISTORY_PAGE_SIZE = 10
const TYPE_TABS: { key: TimelineType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'conversation', label: 'Tickets' },
  { key: 'call', label: 'Calls' },
  { key: 'live_chat', label: 'Live chats' },
  { key: 'voicemail', label: 'Voicemail' },
  { key: 'csat', label: 'CSAT' },
  { key: 'form_submission', label: 'Forms' },
]

const TYPE_ICON: Record<TimelineItem['type'], JSX.Element> = {
  conversation: <MessageSquare className="h-3.5 w-3.5" />,
  call: <PhoneCall className="h-3.5 w-3.5" />,
  live_chat: <MessageSquare className="h-3.5 w-3.5" />,
  voicemail: <Voicemail className="h-3.5 w-3.5" />,
  csat: <Star className="h-3.5 w-3.5" />,
  form_submission: <ClipboardList className="h-3.5 w-3.5" />,
}

const REASON_LABEL: Record<string, string> = {
  same_requester: 'Same requester handle',
  similar_subject: 'Similar subject',
  same_category: 'Same category',
}

function StatusPill({ status }: { status: string | null }): JSX.Element {
  if (!status) return <span className="text-slate-300">—</span>
  const n = status.toLowerCase()
  const tone = /completed|closed|resolved|answered|heard/.test(n)
    ? 'bg-emerald-50 text-emerald-700'
    : /open|reopen|active|new|in_progress/.test(n)
      ? 'bg-blue-50 text-blue-700'
      : /hold|pending|deferred/.test(n)
        ? 'bg-amber-50 text-amber-700'
        : /fail|abandon|no_answer|busy|deleted/.test(n)
          ? 'bg-red-50 text-red-600'
          : 'bg-slate-100 text-slate-600'
  return (
    <span className={`inline-flex whitespace-nowrap rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function Tile({ icon, value, label, sub }: { icon: JSX.Element; value: string; label: string; sub: string }): JSX.Element {
  return (
    <div className={`flex items-center gap-3 ${R} border border-slate-200 bg-white px-4 py-3`}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">{icon}</div>
      <div className="min-w-0">
        <p className="text-lg font-bold leading-tight text-[#0f172a]">{value}</p>
        <p className="truncate text-[11px] font-medium text-slate-500">
          {label} <span className="text-slate-400">{sub}</span>
        </p>
      </div>
    </div>
  )
}

function SectionHeader({ title, border = false }: { title: string; border?: boolean }): JSX.Element {
  return (
    <h3 className={`bg-slate-50/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${border ? 'border-y' : 'border-b'} border-slate-100`}>
      {title}
    </h3>
  )
}

const BADGE_TONE = {
  green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  red: 'border-red-200 bg-red-50 text-red-600',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
} as const

function Badge({ tone, children }: { tone: keyof typeof BADGE_TONE; children: string }): JSX.Element {
  return (
    <span className={`inline-flex items-center ${R} border px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONE[tone]}`}>{children}</span>
  )
}

/** One label/value line; text values get an optional copy button on hover. */
function DetailRow({ icon, label, value, mono = false, onCopy }: {
  icon: JSX.Element
  label: string
  value: ReactNode
  mono?: boolean
  onCopy?: (text: string, label: string) => void
}): JSX.Element {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className="group flex items-center gap-3 px-4 py-2.5">
      <span className="text-slate-400">{icon}</span>
      <dt className="w-32 shrink-0 text-xs font-medium text-slate-500">{label}</dt>
      <dd className={`min-w-0 flex-1 truncate text-[13px] ${empty ? 'text-slate-300' : 'font-medium text-slate-800'} ${mono && !empty ? 'font-mono' : ''}`}>
        {empty ? '—' : value}
      </dd>
      {onCopy && typeof value === 'string' && value && (
        <button
          type="button"
          onClick={() => onCopy(value, label)}
          className={`${R} p-1 text-slate-300 opacity-0 transition hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100`}
          title={`Copy ${label.toLowerCase()}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

function toCandidate(s: RelatedSide): MergeCandidate {
  return { id: s.id, referenceId: s.referenceId, subject: s.subject, status: s.status, requester: null }
}

/* ── Page ───────────────────────────────────────────────────────────── */

export default function Customer360Page(): JSX.Element {
  const { clientId = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [overview, setOverview] = useState<OverviewResponse | null>(null)
  const [loadingOverview, setLoadingOverview] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [tab, setTab] = useState<'basic' | 'timeline' | 'related' | 'history'>('basic')
  const [type, setType] = useState<TimelineType>('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null)
  const [loadingTimeline, setLoadingTimeline] = useState(false)

  const [related, setRelated] = useState<RelatedItem[] | null>(null)
  const [loadingRelated, setLoadingRelated] = useState(false)
  const [historyItems, setHistoryItems] = useState<CustomerHistoryItem[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyPage, setHistoryPage] = useState(1)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [actingPair, setActingPair] = useState<string | null>(null)
  const [mergePair, setMergePair] = useState<RelatedItem | null>(null)
  const [selectedSubmission, setSelectedSubmission] = useState<TimelineItem | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = useCallback(() => setRefreshKey((v) => v + 1), [])
  const { mergeInto } = useConversationActions(null, refresh)

  useEffect(() => {
    if (!clientId) return
    setLoadingOverview(true)
    api.get<OverviewResponse>(`/agent/customers/${clientId}/overview`)
      .then(setOverview)
      .catch(() => setNotFound(true))
      .finally(() => setLoadingOverview(false))
  }, [clientId, refreshKey])

  useEffect(() => {
    if (!clientId || tab !== 'timeline') return
    const params = new URLSearchParams({ type, page: String(page), limit: '20' })
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    let cancelled = false
    setLoadingTimeline(true)
    api.get<TimelineResponse>(`/agent/customers/${clientId}/timeline?${params.toString()}`)
      .then((res) => { if (!cancelled) setTimeline(res) })
      .catch(() => { if (!cancelled) setTimeline(null) })
      .finally(() => { if (!cancelled) setLoadingTimeline(false) })
    return () => { cancelled = true }
  }, [clientId, tab, type, page, from, to, refreshKey])

  useEffect(() => {
    if (!clientId || tab !== 'related') return
    let cancelled = false
    setLoadingRelated(true)
    api.get<{ items: RelatedItem[] }>(`/agent/customers/${clientId}/related`)
      .then((res) => { if (!cancelled) setRelated(res.items) })
      .catch(() => { if (!cancelled) setRelated([]) })
      .finally(() => { if (!cancelled) setLoadingRelated(false) })
    return () => { cancelled = true }
  }, [clientId, tab, refreshKey])

  useEffect(() => {
    if (!clientId || tab !== 'history') return
    let cancelled = false
    setLoadingHistory(true)
    const limit = HISTORY_PAGE_SIZE
    const offset = (historyPage - 1) * limit
    api.get<CustomerHistoryResponse>(`/agent/clients/${clientId}/audit-log?limit=${limit}&offset=${offset}`)
      .then((res) => {
        if (!cancelled) {
          setHistoryItems(res.rows)
          setHistoryTotal(res.total)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryItems([])
          setHistoryTotal(0)
        }
      })
      .finally(() => { if (!cancelled) setLoadingHistory(false) })
    return () => { cancelled = true }
  }, [clientId, tab, refreshKey, historyPage])

  const client = overview?.client ?? null
  const stats = overview?.stats

  const copy = (text: string, label: string): void => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed'),
    )
  }

  const linkPair = async (item: RelatedItem): Promise<void> => {
    const key = `${item.a.id}|${item.b.id}`
    setActingPair(key)
    try {
      await api.post(`/conversations/${item.a.id}/link`, {
        conversationBId: item.b.id,
        reason: `Customer 360: ${item.reasons.map((r) => REASON_LABEL[r] ?? r).join(', ')}`,
      })
      toast.success('Conversations linked', `#${item.a.referenceId} ↔ #${item.b.referenceId}`)
      setRelated((prev) => prev?.filter((p) => !(p.a.id === item.a.id && p.b.id === item.b.id)) ?? prev)
    } catch (e) {
      toast.error('Link failed', e instanceof Error ? e.message : 'Could not link conversations')
    } finally {
      setActingPair(null)
    }
  }

  const mergeCandidates = useMemo<MergeCandidate[]>(
    () => (mergePair ? [toCandidate(mergePair.a), toCandidate(mergePair.b)] : []),
    [mergePair],
  )

  const handleMergeConfirm = async (input: MergeSubmit) => {
    const res = await mergeInto(input.primaryId, input.secondaryIds, {
      note: input.note,
      noteIsPublic: input.noteIsPublic,
      addSecondaryRecipientsToCc: input.addSecondaryRecipientsToCc,
      allowDifferentRequesters: input.allowDifferentRequesters,
    })
    if (res.ok && mergePair) {
      setRelated((prev) => prev?.filter((p) => p !== mergePair) ?? prev)
      setMergePair(null)
    }
    return res
  }

  const totalPages = Math.max(1, Math.ceil((timeline?.total ?? 0) / (timeline?.limit ?? 20)))
  const timelineItems = timeline?.items ?? []

  const openItem = (item: TimelineItem): void => {
    if (item.type === 'form_submission' && item.formData) {
      setSelectedSubmission(item)
      return
    }
    if (item.conversationId) navigate(`/agent/conversation/view/${item.conversationId}`)
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-[#F4F6F8]">
      {/* Header */}
      <header className="flex flex-wrap items-center gap-4 border-b border-slate-200 bg-white px-5 py-4">
        <button
          onClick={() => navigate('/agent/customers')}
          className={`flex h-9 w-9 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 transition-colors hover:text-slate-900`}
          title="Back to customer search"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-12 w-12 items-center justify-center rounded-[3px] bg-[#EDE9FE] text-base font-bold text-[#6D28D9]">
          {(client?.fullName ?? client?.email ?? '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-lg font-bold text-[#0f172a]">
              {loadingOverview ? 'Loading…' : (client?.fullName || client?.email || 'Unknown customer')}
            </h1>
            {client && (
              <span className={`rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ${client.status ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                {client.status ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {[client?.email, client?.phone, client?.groupName].filter(Boolean).join(' · ') || 'No contact details'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {client && (
            <button
              onClick={() => setEditOpen(true)}
              className={`flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700`}
              title="Edit customer details"
            >
              <Pencil className="h-3.5 w-3.5" />Edit
            </button>
          )}
          <button onClick={refresh} className={`flex h-9 w-9 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 hover:text-slate-900`} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </header>

      {notFound ? (
        <div className="flex flex-1 flex-col items-center justify-center py-24">
          <Contact className="mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">Customer not found.</p>
          <Link to="/agent/customers" className="mt-2 text-sm font-semibold text-[#3366FF] hover:underline">
            Search customers
          </Link>
        </div>
      ) : (
        <div className="flex-1 space-y-4 p-5">
          {/* KPI tiles */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Tile icon={<AlertTriangle className="h-4 w-4" />} value={String(stats?.openConversations ?? 0)} label="Open" sub="concerns" />
            <Tile icon={<MessageSquare className="h-4 w-4" />} value={String(stats?.totalConversations ?? 0)} label="Tickets" sub="all channels" />
            <Tile icon={<PhoneCall className="h-4 w-4" />} value={String(stats?.totalCalls ?? 0)} label="Calls" sub="all time" />
            <Tile icon={<Users className="h-4 w-4" />} value={String(stats?.totalLiveChats ?? 0)} label="Live chats" sub="all time" />
            <Tile icon={<Voicemail className="h-4 w-4" />} value={String(stats?.totalVoicemails ?? 0)} label="Voicemails" sub="all time" />
            <Tile
              icon={<Star className="h-4 w-4" />}
              value={stats?.avgCsat != null ? `${stats.avgCsat}` : '—'}
              label="Avg CSAT"
              sub={stats?.csatCount ? `${stats.csatCount} surveys` : 'no surveys'}
            />
          </div>

          {/* Identities */}
          {(overview?.identities.length ?? 0) > 0 && (
            <div className={`${R} border border-slate-200 bg-white px-4 py-3`}>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Known identities</p>
              <div className="flex flex-wrap gap-1.5">
                {overview!.identities.map((i, idx) => {
                  const ch = getChannelConfig(i.channel)
                  const Icon = ch.icon
                  
                  // Generate profile link for Facebook and Instagram
                  const profileLink = 
                    i.channel === 'facebook' && i.handle
                      ? `https://facebook.com/${i.handle}`
                      : i.channel === 'instagram' && i.handle
                        ? `https://instagram.com/${i.handle.replace('@', '')}`
                        : null
                  
                  const badge = (
                    <span className={`inline-flex items-center gap-1.5 ${R} ${ch.bg} px-2 py-1 text-[11px] font-medium ${ch.color}`}>
                      <Icon className="h-3 w-3" />
                      <span className="font-mono">{i.handle}</span>
                      <span className="text-[10px] opacity-60">{i.source.replace('_', ' ')}</span>
                    </span>
                  )
                  
                  // If profile link exists, wrap in clickable link
                  return profileLink ? (
                    <a
                      key={`${i.channel}:${i.handle}:${idx}`}
                      href={profileLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-opacity hover:opacity-75"
                      title={`View ${i.channel === 'facebook' ? 'Facebook' : 'Instagram'} profile`}
                    >
                      {badge}
                    </a>
                  ) : (
                    <span key={`${i.channel}:${i.handle}:${idx}`}>
                      {badge}
                    </span>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className={`flex items-center gap-1 ${R} w-fit border border-slate-200 bg-slate-100 p-1`}>
            {[
              { key: 'basic', label: 'Basic Info', icon: Contact },
              { key: 'timeline', label: 'Timeline', icon: History },
              { key: 'related', label: 'Related concerns', icon: ScanSearch },
              { key: 'history', label: 'History', icon: ClipboardList },
            ].map((t) => {
              const Icon = t.icon
              const on = tab === t.key
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key as typeof tab)}
                  className={`flex items-center gap-2 ${R} px-3.5 py-1.5 text-[13px] font-semibold transition-all ${on ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}><Icon className="h-4 w-4" /></span>
                  {t.label}
                </button>
              )
            })}
          </div>

          {tab === 'basic' && (
            loadingOverview ? (
              <div className={`flex items-center justify-center ${R} border border-slate-200 bg-white py-16 text-sm text-slate-400`}>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading customer details…
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                {/* Contact + account */}
                <section className={`${R} border border-slate-200 bg-white`}>
                  <SectionHeader title="Contact details" />
                  <dl className="divide-y divide-slate-100">
                    <DetailRow icon={<UserRound className="h-4 w-4" />} label="Full name" value={client?.fullName} />
                    <DetailRow icon={<Mail className="h-4 w-4" />} label="Email" value={client?.email} mono onCopy={copy} />
                    <DetailRow icon={<Phone className="h-4 w-4" />} label="Phone" value={client?.phone} mono onCopy={copy} />
                  </dl>
                  <SectionHeader title="Account" border />
                  <dl className="divide-y divide-slate-100">
                    <DetailRow icon={<Users className="h-4 w-4" />} label="Client group" value={client?.groupName || 'No group'} />
                    <DetailRow
                      icon={<ShieldCheck className="h-4 w-4" />}
                      label="Status"
                      value={client ? <Badge tone={client.status ? 'green' : 'red'}>{client.status ? 'Active' : 'Inactive'}</Badge> : null}
                    />
                    <DetailRow
                      icon={client?.locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                      label="Portal access"
                      value={client ? <Badge tone={client.locked ? 'amber' : 'slate'}>{client.locked ? 'Locked' : 'Unlocked'}</Badge> : null}
                    />
                  </dl>
                </section>

                {/* Engagement summary */}
                <section className={`${R} border border-slate-200 bg-white`}>
                  <SectionHeader title="Engagement" />
                  <dl className="divide-y divide-slate-100">
                    <DetailRow
                      icon={<CalendarClock className="h-4 w-4" />}
                      label="First contact"
                      value={stats?.firstContactAt ? formatDateTime(stats.firstContactAt) : null}
                    />
                    <DetailRow
                      icon={<History className="h-4 w-4" />}
                      label="Last contact"
                      value={stats?.lastContactAt ? formatDateTime(stats.lastContactAt) : null}
                    />
                  </dl>
                  <SectionHeader title="Tickets by channel" border />
                  {(overview?.conversationsByChannel.length ?? 0) === 0 ? (
                    <p className="px-4 py-4 text-xs text-slate-400">No tickets yet.</p>
                  ) : (
                    <ul className="space-y-2.5 px-4 py-3.5">
                      {overview!.conversationsByChannel.map((c) => {
                        const ch = getChannelConfig(c.channel)
                        const Icon = ch.icon
                        const max = Math.max(...overview!.conversationsByChannel.map((x) => x.count), 1)
                        return (
                          <li key={c.channel}>
                            <div className="mb-1 flex items-center justify-between text-xs">
                              <span className="flex items-center gap-1.5 font-medium text-slate-700">
                                <Icon className={`h-3.5 w-3.5 ${ch.color}`} />{ch.label}
                              </span>
                              <span className="font-mono font-semibold tabular-nums text-slate-900">{c.count}</span>
                            </div>
                            <div className={`h-1.5 overflow-hidden ${R} bg-slate-100`}>
                              <div className={`h-full ${R} bg-[#3366FF]`} style={{ width: `${(c.count / max) * 100}%` }} />
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </section>
              </div>
            )
          )}

          {tab === 'history' && (
            <div className={`${R} border border-slate-200 bg-white`}>
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Customer history</h2>
                  <p className="mt-1 text-xs text-slate-500">Audit log of actions and updates related to this customer.</p>
                </div>
                <span className="rounded-[3px] border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                  {historyTotal.toLocaleString()} {historyTotal === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              {loadingHistory ? (
                <div className="space-y-3 p-5" aria-label="Loading history">
                  {Array.from({ length: 4 }, (_, index) => <div key={index} className="h-[62px] animate-pulse rounded-[3px] bg-slate-50" />)}
                </div>
              ) : historyItems.length === 0 ? (
                <div className="flex flex-col items-center px-5 py-12 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-[3px] bg-slate-100 text-slate-400"><History className="h-5 w-5" /></span>
                  <p className="text-sm font-semibold text-slate-700">No history yet</p>
                  <p className="mt-1 text-xs text-slate-500">Customer actions will appear here when they are recorded.</p>
                </div>
              ) : (
                <ol className="divide-y divide-slate-100 px-5">
                  {historyItems.map((entry) => {
                    const actionLabel = entry.action.replace(/[._]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
                    return (
                      <li key={entry.id} className="flex gap-3 py-4 first:pt-5 last:pb-5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
                          <ClipboardList className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                            <p className="text-sm font-semibold text-slate-800">{actionLabel}</p>
                            <time className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-slate-500" title={new Date(entry.createdAt).toLocaleString()}>
                              <CalendarClock className="h-3.5 w-3.5 text-slate-400" />{formatDateTime(entry.createdAt)}
                            </time>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 rounded-[3px] bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              <UserRound className="h-3 w-3" />{entry.actorName || 'System'}
                            </span>
                            {entry.resourceType && <span className="rounded-[3px] bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">{entry.resourceType.replace(/_/g, ' ')}</span>}
                            {entry.resourceId && <span className="max-w-full truncate font-mono text-[10px] text-slate-400" title={entry.resourceId}>{entry.resourceId}</span>}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              )}
              {historyTotal > HISTORY_PAGE_SIZE && (
                <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                  <span className="text-xs text-slate-500">
                    Showing {(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, historyTotal)} of {historyTotal}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage === 1 || loadingHistory}
                      className="rounded-[3px] border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    >Previous</button>
                    <span className="text-xs text-slate-500">Page {historyPage} of {Math.ceil(historyTotal / HISTORY_PAGE_SIZE)}</span>
                    <button
                      type="button"
                      onClick={() => setHistoryPage((p) => p + 1)}
                      disabled={historyPage >= Math.ceil(historyTotal / HISTORY_PAGE_SIZE) || loadingHistory}
                      className="rounded-[3px] border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    >Next</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timeline tab */}
          {tab === 'timeline' && (
            <div className={`${R} border border-slate-200 bg-white`}>
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
                {TYPE_TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setType(t.key); setPage(1) }}
                    className={`${R} px-2.5 py-1 text-[12px] font-semibold transition-colors ${type === t.key ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-500 hover:bg-slate-100'}`}
                  >
                    {t.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2 text-[12px] text-slate-500">
                                    <DatePicker
                    ariaLabel="From"
                    size="sm"
                    label="From"
                    value={from}
                    onChange={(v) => { setFrom(v); setPage(1) }}
                  />
                                    <DatePicker
                    ariaLabel="To"
                    size="sm"
                    label="To"
                    value={to}
                    onChange={(v) => { setTo(v); setPage(1) }}
                  />
                  {(from || to) && (
                    <button onClick={() => { setFrom(''); setTo(''); setPage(1) }} className="text-[#3366FF] hover:underline">Clear</button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-xs font-semibold text-slate-500">
                      <th className="px-4 py-2.5">Type</th>
                      <th className="px-4 py-2.5">Reference</th>
                      <th className="px-4 py-2.5">Details</th>
                      <th className="px-4 py-2.5">Channel</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">Handled by</th>
                      <th className="px-4 py-2.5">When</th>
                      <th className="px-4 py-2.5 text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingTimeline && (
                      <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading timeline…</td></tr>
                    )}
                    {!loadingTimeline && timelineItems.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">No interactions match these filters.</td></tr>
                    )}
                    {!loadingTimeline && timelineItems.map((item) => {
                      const ch = getChannelConfig(item.channel)
                      const Icon = ch.icon
                      return (
                        <tr
                          key={`${item.type}-${item.id}`}
                          onClick={() => openItem(item)}
                          className={`border-b border-slate-50 last:border-0 ${item.conversationId || item.type === 'form_submission' ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                        >
                          <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">{TYPE_ICON[item.type]}{item.type.replace('_', ' ')}</span></td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{item.ref ?? '—'}</td>
                          <td className="max-w-[20rem] truncate px-4 py-2.5 text-[13px] text-slate-800">{item.title}{item.isMerged && <span className="ml-1.5 text-[10px] text-slate-400">(merged)</span>}</td>
                          <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 text-xs ${ch.color}`}><Icon className="h-3 w-3" />{ch.label}</span></td>
                          <td className="px-4 py-2.5"><StatusPill status={item.status} /></td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">{item.actor ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">{formatDateTime(item.at)}</td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-slate-500">{item.durationSec != null ? formatDuration(item.durationSec * 1000) : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-[12px] text-slate-500">
                <span>{timeline?.total ?? 0} interaction{(timeline?.total ?? 0) === 1 ? '' : 's'}</span>
                <div className="flex items-center gap-1">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-7 w-7 items-center justify-center rounded-[3px] text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="px-2">Page {Math.min(page, totalPages)} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="flex h-7 w-7 items-center justify-center rounded-[3px] text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          )}

          {/* Related concerns tab */}
          {tab === 'related' && (
            <div className="space-y-3">
              <p className="text-[12px] text-slate-500">
                Suggestions only — KC3 never merges on its own. Link keeps both tickets open and cross-referenced; merge folds one into the other.
              </p>
              {loadingRelated && (
                <div className={`${R} border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400`}>
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Scanning for related concerns…
                </div>
              )}
              {!loadingRelated && (related?.length ?? 0) === 0 && (
                <div className={`${R} border border-slate-200 bg-white px-4 py-10 text-center`}>
                  <ScanSearch className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                  <p className="text-sm text-slate-500">No related concerns detected for this customer.</p>
                </div>
              )}
              {!loadingRelated && related?.map((item) => {
                const key = `${item.a.id}|${item.b.id}`
                const busy = actingPair === key
                return (
                  <div key={key} className={`${R} border border-slate-200 bg-white p-4`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        {[item.a, item.b].map((s, i) => (
                          <div key={s.id} className={`flex items-center gap-2 ${i === 0 ? 'mb-1.5' : ''}`}>
                            <span className="shrink-0 font-mono text-[11px] text-slate-400">#{s.referenceId}</span>
                            <Link to={`/agent/conversation/view/${s.id}`} className="truncate text-[13px] font-semibold text-[#0f172a] hover:text-[#3366FF]">
                              {s.subject || 'Conversation'}
                            </Link>
                            <StatusPill status={s.status} />
                            <span className="text-[11px] text-slate-400">{s.channel}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex flex-wrap justify-end gap-1">
                          {item.reasons.map((r) => (
                            <span key={r} className="rounded-[3px] bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                              {REASON_LABEL[r] ?? r}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-1.5">
                          <button
                            disabled={busy}
                            onClick={() => void linkPair(item)}
                            className={`inline-flex items-center gap-1 ${R} border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-600 hover:border-[#3366FF] hover:text-[#3366FF] disabled:opacity-40`}
                          >
                            <Link2 className="h-3 w-3" /> Link
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => setMergePair(item)}
                            className={`inline-flex items-center gap-1 ${R} bg-[#3366FF] px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-[#2952CC] disabled:opacity-40`}
                          >
                            <GitMerge className="h-3 w-3" /> Merge…
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      <MergeDrawer
        open={mergePair !== null}
        onClose={() => setMergePair(null)}
        candidates={mergeCandidates}
        defaultPrimaryId={mergeCandidates[0]?.id ?? ''}
        onConfirm={handleMergeConfirm}
      />

      <CreateClientPanel
        open={editOpen}
        mode="edit"
        clientId={clientId}
        noun="Customer"
        onClose={() => setEditOpen(false)}
        onCreated={() => { setEditOpen(false); refresh() }}
      />

      {selectedSubmission?.formData && (
        <FormSubmissionDialog item={selectedSubmission} onClose={() => setSelectedSubmission(null)} />
      )}
    </div>
  )
}

/* ── Portal form submission detail — renders the frozen snapshot so the
   view stays faithful even after the form definition is edited later. ── */
function formatSubmissionValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function FormSubmissionDialog({ item, onClose }: { item: TimelineItem; onClose: () => void }): JSX.Element {
  const data = item.formData as FormSubmissionData
  const fields = data.fields ?? []
  const attachmentsByField = new Map<string, FormSubmissionData['attachments']>()
  for (const att of data.attachments ?? []) {
    const list = attachmentsByField.get(att.fieldId) ?? []
    list.push(att)
    attachmentsByField.set(att.fieldId, list)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className={`w-full max-w-lg ${R} bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-[#0f172a]">{data.formName}</h2>
            <p className="text-[11px] text-slate-400">Form submission · {formatDateTime(item.at)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`flex h-8 w-8 shrink-0 items-center justify-center ${R} text-slate-400 hover:bg-slate-100 hover:text-slate-700`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {fields.length === 0 && Object.keys(data.values ?? {}).length === 0 && (
            <p className="text-sm text-slate-400">No fields were captured in this submission.</p>
          )}
          <dl className="space-y-3">
            {fields
              .filter(
                (field) =>
                  field.type !== 'conversation_ref' &&
                  !field.excludeFromTimeline &&
                  !/conversation\s*(?:id|ref|reference)?/i.test(field.label) &&
                  !/ticket\s*(?:id|ref|reference)?/i.test(field.label) &&
                  !/conversation\s*(?:id|ref|reference)?/i.test(field.id) &&
                  !/ticket\s*(?:id|ref|reference)?/i.test(field.id),
              )
              .map((field) => (
              <div key={field.id}>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{field.label}</dt>
                <dd className="mt-0.5 text-[13px] text-slate-800">
                  {field.type === 'file'
                    ? (attachmentsByField.get(field.id)?.length ?? 0) === 0
                      ? '—'
                      : (
                        <ul className="space-y-1">
                          {(attachmentsByField.get(field.id) ?? []).map((att) => (
                            <li key={att.id}>
                              <a
                                href={`/api/admin/portal-forms/attachments/${att.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#3366FF] hover:underline"
                              >
                                {att.filename}
                                <span className="text-[11px] text-slate-400">({Math.max(1, Math.round(att.sizeBytes / 1024))} KB)</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                        )
                    : formatSubmissionValue(data.values?.[field.id])}
                </dd>
              </div>
            ))}
            {/* Values with no matching field in the snapshot (e.g. a field
                removed after capture — snapshot keeps history honest). */}
            {Object.entries(data.values ?? {})
              .filter(([key]) => !fields.some((f) => f.id === key) && !['conversation_id', 'conversationRef', 'conversation_ref'].includes(key))
              .map(([key, value]) => (
                <div key={key}>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{key}</dt>
                  <dd className="mt-0.5 text-[13px] text-slate-800">{formatSubmissionValue(value)}</dd>
                </div>
              ))}
          </dl>
        </div>
      </div>
    </div>
  )
}
